/**
 * Buyer preview page (P-1.13).
 *
 * Routed at /preview/:flowId/:nodeId — renders the buyer-facing checkout for
 * a given checkout_page node inside a given flow. Uses the shared renderer
 * components (features/checkout-renderer/blocks.tsx) so it looks identical
 * to what the page builder shows.
 *
 * Mock cart data; "Pay" button shows a success splash. No real payment.
 * Phase 2 replaces this page with the real hosted-frontend implementation.
 */

import { useEffect, useState, type MouseEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Monitor, Smartphone } from "lucide-react";
import { clsx } from "clsx";
import { getApiClient } from "../lib/apiClient";
import { ApiError } from "../types/api";
import type { CheckoutPageNode } from "../types/flow";
import { CheckoutPagePreview } from "../features/checkout-renderer/blocks";

export function BuyerPreviewPage() {
  const { flowId, nodeId } = useParams<{ flowId: string; nodeId: string }>();
  const [search] = useSearchParams();
  const [node, setNode] = useState<CheckoutPageNode | null>(null);
  const [shopifyShopDomain, setShopifyShopDomain] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<"desktop" | "mobile">(
    (search.get("v") as "desktop" | "mobile") ?? "desktop",
  );
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (!flowId || !nodeId) return;
    let cancelled = false;
    (async () => {
      try {
        const versions = await getApiClient().versions.list(flowId);
        const latest =
          versions.find((v) => v.state === "published") ??
          versions[versions.length - 1];
        if (!latest) {
          if (!cancelled) setError("No versions on this flow yet.");
          return;
        }
        const target = latest.definition.nodes[nodeId];
        if (!target) {
          if (!cancelled) setError(`Node "${nodeId}" not found in flow.`);
          return;
        }
        if (target.type !== "checkout_page") {
          if (!cancelled) setError(`Node "${nodeId}" is not a checkout_page.`);
          return;
        }
        if (!cancelled) {
          setNode(target);
          // Pull Shopify shop domain off the trigger so the preview's
          // topbar can render the Shopify cart icon (matches buyer page).
          const start = latest.definition.nodes[latest.definition.start_node];
          if (start && start.type === "trigger") {
            const sh = start.sources.find((s) => s.type === "shopify" && s.reference);
            setShopifyShopDomain(sh?.reference ?? null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Failed to load flow",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flowId, nodeId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md text-center">
          <h2 className="text-lg font-semibold mb-2">Preview unavailable</h2>
          <p className="text-sm text-neutral-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!node) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-500">Loading preview…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-950 flex flex-col">
      <div className="bg-amber-100 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900 px-6 py-2 text-xs text-amber-900 dark:text-amber-200">
        <strong>Preview.</strong> Buyer-side render of what a customer would see
        for this checkout_page node. "Pay" shows a success splash — no real
        payment. Phase 2 replaces this with the live hosted frontend.
      </div>

      <div className="flex items-center justify-center p-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex rounded-md border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <button
            onClick={() => setViewport("desktop")}
            className={clsx(
              "px-3 py-1.5 text-xs flex items-center gap-1",
              viewport === "desktop"
                ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900"
                : "text-neutral-500",
            )}
          >
            <Monitor className="w-3.5 h-3.5" />
            Desktop
          </button>
          <button
            onClick={() => setViewport("mobile")}
            className={clsx(
              "px-3 py-1.5 text-xs flex items-center gap-1",
              viewport === "mobile"
                ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900"
                : "text-neutral-500",
            )}
          >
            <Smartphone className="w-3.5 h-3.5" />
            Mobile
          </button>
        </div>
      </div>

      <main className="flex-1 p-6 flex justify-center">
        <div className={clsx("w-full", viewport === "mobile" ? "max-w-sm" : "max-w-5xl")}>
          {paid ? (
            <div className="rounded-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-10 text-center">
              <h1 className="text-2xl font-semibold mb-2">Thanks — your order is confirmed.</h1>
              <p className="text-sm text-neutral-500 mb-5">
                (In Studio mode, no real payment was taken. This is where
                customers would land on Shopify's native thank-you page or
                your configured success URL.)
              </p>
              <button
                onClick={() => setPaid(false)}
                className="rounded-md border border-neutral-300 dark:border-neutral-700 px-3 py-1.5 text-sm"
              >
                Restart preview
              </button>
            </div>
          ) : (
            <div onClickCapture={handlePayIfButton(() => setPaid(true))}>
              <CheckoutPagePreview
                node={node}
                viewport={viewport}
                shopifyShopDomain={shopifyShopDomain}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function handlePayIfButton(cb: () => void) {
  return (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target instanceof HTMLButtonElement && target.textContent?.toLowerCase().includes("pay")) {
      e.preventDefault();
      e.stopPropagation();
      cb();
    }
  };
}
